import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from "../../api/axios";
import moment from 'moment';
import { UserIcon, PhoneIcon, MapPinIcon } from "@heroicons/react/24/outline";

const TransactionDetails = () => {
    const { transactionId } = useParams();
    const navigate = useNavigate();

    const [transaction, setTransaction] = useState(null);
    const [details, setDetails] = useState({});
    const [items, setItems] = useState([]);
    const [bank, setBank] = useState({});
    const [paymentList, setPaymentList] = useState([]);
    const [paidAmount, setPaidAmount] = useState(0);
    const [contactInfo, setContactInfo] = useState(null);
    const formatBDT = (value) => Number(value || 0).toFixed(2);
    useEffect(() => {
        if (transactionId) {
            axios.get(`/bank-transactions/transactions/${transactionId}/details`)
                .then(async res => {
                    const { transaction, details } = res.data;
                    setTransaction(transaction);
                    setDetails(details);
                    setItems(details.items || []);
                    if (transaction.reference_type === 'sale') {
                        const sale = details;
                        const customerId = sale.customer_id;
                        const res = await axios.get(`/customers/${customerId}`);
                        setContactInfo(res.data);
                    }

                    if (transaction.reference_type === 'purchase') {
                        const purchase = details;
                        const supplierId = purchase.supplier_id;
                        const res = await axios.get(`/suppliers/${supplierId}`);
                        setContactInfo(res.data);
                    }
                    if (transaction.reference_type === 'sale' || transaction.reference_type === 'purchase') {
                        const type = transaction.reference_type;
                        const id = transaction.reference_id;
                        const paymentRes = await axios.get(`/${type}s/${id}/payments`);
                        setPaymentList(paymentRes.data);
                        const payments = paymentRes.data;
                        const totalPaid = payments.reduce((sum, p) => sum + Number(p.amount || 0), 0);
                        setPaidAmount(totalPaid);
                    }
                })
                .catch(err => console.error('Error loading transaction:', err));
        }
    }, [transactionId]);

    if (!transaction) return <div className="p-4">Loading...</div>;

    const subtotal = items.reduce((sum, item) => sum + Number(item.total || 0), 0);
    const totalQty = items.reduce((sum, item) => sum + Number(item.quantity || 0), 0);

    const capitalize = str => str?.charAt(0).toUpperCase() + str.slice(1);
    const discount = details?.discount || 0;
    const discountType = details?.discount_type || 'flat';
    const otherCharges = Number(details?.other_charges || 0);

    const discountValue = discountType === 'percent'
        ? (subtotal * discount) / 100
        : discount;

    const grandTotal = subtotal + otherCharges - discountValue;
    const dueAmount = Math.max(grandTotal - paidAmount, 0).toFixed(2);
    return (
        <div className="p-4 max-w-6xl mx-auto bg-white rounded">
            <div className='flex justify-between items-center border-b-4 mb-3 pb-2'>
                <h2 className="text-xl font-bold">Transaction Details: TXN-{transaction.id}</h2>
                <p>Date: {moment(transaction.transaction_date).format("DD-MM-YYYY hh:mm:ss A")}</p>
            </div>

            <div className="grid md:grid-cols-3 gap-4 mb-4">
                <div className='flex flex-col'>
                    <span className="font-semibold text-sm">From</span>
                    <span className='text-[12px] font-medium flex items-center'><UserIcon className="w-3 h-3 mr-1" />MaanRishfa IT</span>
                    <span className='text-[12px] flex items-center'><PhoneIcon className="w-3 h-3 mr-1" />0192-5325050</span>
                    <span className='text-[12px] italic flex items-center'><MapPinIcon className="w-3 h-3 mr-1" />Noapara, Jessore</span>
                </div>

                <div className='flex flex-col'>
                    <span className="font-semibold text-sm">
                        {transaction.reference_type === 'sale' ? 'Customer Details' : 'Supplier Details'}
                    </span>
                    <span className='text-[12px] font-medium flex items-center'><UserIcon className="w-3 h-3 mr-1" />{contactInfo?.name || 'N/A'}</span>
                    <span className='text-[12px] flex items-center'><PhoneIcon className="w-3 h-3 mr-1" />{contactInfo?.phone || 'N/A'}</span>
                    <span className='text-[12px] italic flex items-center'><MapPinIcon className="w-3 h-3 mr-1" />{contactInfo?.address || 'N/A'}</span>
                </div>

                <div className='flex flex-col items-end'>
                    <span className='font-semibold text-sm'>Type: {capitalize(transaction.type)}</span>
                    <span className='text-[13px]'>Amount: ৳ {transaction.amount}</span>
                    <span className='text-[13px] italic'>Reference: {transaction.reference || "N/A"}</span>
                    <span className='text-[13px] italic'>Status: {capitalize(transaction.status)}</span>
                </div>
            </div>

            {items.length > 0 && (
                <table className="w-full mt-4 border text-xs">
                    <thead className="bg-blue-800 text-white">
                        <tr>
                            <th className="border px-2 py-1">Item Name</th>
                            <th className="border px-2 py-1">Quantity</th>
                            <th className="border px-2 py-1">Unit Price</th>
                            <th className="border px-2 py-1">Discount</th>
                            <th className="border px-2 py-1">Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((item, index) => (
                            <tr className="odd:bg-white even:bg-gray-100" key={index}>
                                <td className="border px-2 py-1">{item.item_name}</td>
                                <td className="border px-2 py-1 text-center">{item.quantity}</td>
                                <td className="border px-2 py-1 text-end">{item.price}</td>
                                <td className="border px-2 py-1 text-end">{item.discount || 0}</td>
                                <td className="border px-2 py-1 text-end font-semibold">{item.total}</td>
                            </tr>
                        ))}
                        <tr className="bg-blue-300">
                            <td className="border px-2 py-1 text-end font-bold" colSpan={1}>Total:</td>
                            <td className="border px-2 py-1 text-center font-bold">{totalQty}</td>
                            <td colSpan={2} className="border px-2 py-1 text-center"> </td>
                            <td className="border px-2 py-1 text-end font-bold">৳ {formatBDT(subtotal)}</td>
                        </tr>
                    </tbody>
                </table>
            )}
            <div className="flex justify-between mt-6 items-start">
                <div className="flex flex-col md:w-2/3">
                    {paymentList.length > 0 && (
                        <div className="mt-6">
                            <p className="text-blue-800 mb-2 font-semibold">Payments History:</p>
                            <table className="w-full mt-2 border text-xs">
                                <thead className="bg-blue-800 text-white">
                                    <tr>
                                        <th className="border px-2 py-1">#</th>
                                        <th className="border px-2 py-1">Date</th>
                                        <th className="border px-2 py-1">Bank Name</th>
                                        <th className="border px-2 py-1">Amount</th>
                                        <th className="border px-2 py-1">Type</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paymentList.map((item, i) => (
                                        <tr key={i} className="odd:bg-white even:bg-gray-100">
                                            <td className="border px-2 py-1">{i + 1}</td>
                                            <td className="border px-2 py-1 text-center">
                                                {moment(item.created_at).format("DD-MM-YYYY hh:mm:ss A")}
                                            </td>
                                            <td className="border px-2 py-1 text-center">{item.bank_name}</td>
                                            <td className="border px-2 py-1 text-center">{formatBDT(item.amount)}</td>
                                            <td className="border px-2 py-1 text-center">{item.type}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
                <div className="w-full md:w-1/3 md:justify-end">
                    <div className="text-right mt-6 space-y-1">
                        <div>Subtotal: <strong>{formatBDT(subtotal)} BDT</strong></div>
                        <div>Other Charges: <strong>{formatBDT(otherCharges)} BDT</strong></div>
                        <div>Discount: <strong>{formatBDT(discountValue)} BDT</strong></div>
                        <div className="font-semibold">Grand Total: <strong>{formatBDT(grandTotal)} BDT</strong></div>
                        <div className="font-semibold text-green-600">Paid: <strong>{formatBDT(paidAmount)} BDT</strong></div>
                        <div className="font-semibold text-red-600">Due: <strong>{dueAmount} BDT</strong></div>
                    </div>
                </div>
            </div>

            <div className="flex justify-between mt-6">
                <button onClick={() => navigate('/transactions')} className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded">Back</button>
                {/* Optional edit or print buttons can be added here */}
            </div>
        </div>
    );
};

export default TransactionDetails;